import React, { useCallback, useMemo } from 'react';
import { useFormikContext } from 'formik';
import i18n from 'i18n';
import { FormConfig } from 'components/common/form/FormConfig';
import { FormContent } from 'components/common/form/Form';
import { ChannelGroupFormData } from 'core/channelGroup/ChannelGroup';
import {
  EPAPER_DEFAULT_IMAGE_VALID_TYPES,
  validateEpaperDefaultImage
} from './epaperDefaultImageValidator';

/**
 * 電子紙渠道的預設圖片 —— 裝置沒有廣告可投時的填充圖。ECOPD-5581。
 *
 * 尺寸提示與驗證都取自該渠道由 RMax 同步來的螢幕解析度，不是寫死常數：
 * 正式環境三個電子紙渠道分別是 13 / 32 / 40 吋，各有各的解析度。
 *
 * 圖片不經後端 —— 檔案直傳 cloud storage，PUT 只送 URL 字串
 * （見 ChannelGroupWebService.resolveEpaperDefaultImageUrl，與經銷商 logo 同一套）。
 */
export const ChannelGroupEpaperDefaultImageForm: React.FC = () => {
  const { values } = useFormikContext<ChannelGroupFormData>();
  const screenProperty = values.screenProperty;
  const screenPixelWidth = screenProperty?.screenPixelWidth;
  const screenPixelHeight = screenProperty?.screenPixelHeight;

  const validate = useCallback(
    (fileData: ImageFieldData) =>
      validateEpaperDefaultImage(fileData, screenProperty),
    [screenProperty]
  );

  const formConfig = useMemo(() => {
    const hints = [i18n.t('creativeSetupFlow.labels.typeHint')];
    if (screenPixelWidth && screenPixelHeight) {
      hints.push(
        i18n.t('creativeSetupFlow.labels.imageSizeHint', {
          size: `${screenPixelWidth} x ${screenPixelHeight}`
        })
      );
    }
    hints.push(
      i18n.t('creativeSetupFlow.labels.storageHint', { storage: '2MB' })
    );

    const fieldsBuilder = new FormConfig.FieldsBuilder().addFormikFileInput({
      type: 'image',
      label: i18n.t('channelGroupForm.labels.epaperDefaultImage'),
      name: 'epaperDefaultImageData',
      validTypes: EPAPER_DEFAULT_IMAGE_VALID_TYPES,
      hints,
      validate
    });

    return new FormConfig.Builder()
      .addSection(
        new FormConfig.SectionBuilder(fieldsBuilder.build())
          .withTitle(i18n.t('channelGroups.titles.epaperDefaultImageContent'))
          .build()
      )
      .build();
  }, [screenPixelWidth, screenPixelHeight, validate]);

  return <FormContent formConfig={formConfig} />;
};
